import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Avatar } from '@heroui/react';
import { useTokenLogo } from '@/hooks/tokens/useTokenLogo';
import type { ChainType } from '@/constants/chain-specific/chains';
import type { Address } from 'viem';
interface TokenLogoProps {
  chainId: ChainType;
  tokenAddress: Address;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  fallbackText?: string;
  showLoading?: boolean;
}

/**
 * Performant token logo component with smart caching
 * Uses local token index first, then API fallback with caching
 */
export function TokenLogo(t0) {
  const $ = _c(14);
  const {
    chainId,
    tokenAddress,
    size: t1,
    className,
    fallbackText,
    showLoading: t2
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const showLoading = t2 === undefined ? false : t2;
  const {
    data: tokenLogo,
    isLoading
  } = useTokenLogo(chainId, tokenAddress);
  if (showLoading && isLoading) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="animate-pulse bg-gray-300 rounded-full w-full h-full" />;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    let t4;
    if ($[1] !== className || $[2] !== size) {
      t4 = <Avatar size={size} className={className} showFallback={true} fallback={t3} />;
      $[1] = className;
      $[2] = size;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  let t3;
  if ($[4] !== fallbackText || $[5] !== tokenAddress || $[6] !== tokenLogo?.symbol) {
    t3 = fallbackText || tokenLogo?.symbol?.charAt(0).toUpperCase() || tokenAddress.slice(2, 4).toUpperCase();
    $[4] = fallbackText;
    $[5] = tokenAddress;
    $[6] = tokenLogo?.symbol;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const fallback = t3;
  const t4 = tokenLogo?.logo;
  const t5 = tokenLogo?.name || `Token ${tokenAddress}`;
  let t6;
  if ($[8] !== className || $[9] !== fallback || $[10] !== size || $[11] !== t4 || $[12] !== t5) {
    t6 = <Avatar src={t4} size={size} className={className} showFallback={true} fallback={fallback} alt={t5} />;
    $[8] = className;
    $[9] = fallback;
    $[10] = size;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/**
 * Token logo with symbol text
 */
interface TokenLogoWithSymbolProps extends TokenLogoProps {
  showSymbol?: boolean;
  symbolClassName?: string;
}
export function TokenLogoWithSymbol(t0) {
  const $ = _c(13);
  let logoProps;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      showSymbol: t1,
      symbolClassName: t2,
      ...logoProps
    } = t0);
    $[0] = t0;
    $[1] = logoProps;
    $[2] = t1;
    $[3] = t2;
  } else {
    logoProps = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const showSymbol = t1 === undefined ? true : t1;
  const symbolClassName = t2 === undefined ? "text-sm font-medium ml-2" : t2;
  const {
    data: tokenLogo
  } = useTokenLogo(logoProps.chainId, logoProps.tokenAddress);
  let t3;
  if ($[4] !== logoProps) {
    t3 = <TokenLogo {...logoProps} />;
    $[4] = logoProps;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== showSymbol || $[7] !== symbolClassName || $[8] !== tokenLogo) {
    t4 = showSymbol && tokenLogo?.symbol && <span className={symbolClassName}>{tokenLogo.symbol}</span>;
    $[6] = showSymbol;
    $[7] = symbolClassName;
    $[8] = tokenLogo;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex items-center">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export default TokenLogo;